import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  Calculate as CalculateIcon
} from '@mui/icons-material';

const PerVialCalculator = ({ donor }) => {
  // Calculate costs according to requirements
  const rawMaterialsCost = parseFloat(donor?.totalRawMaterialCost || 0);
  const qcTestingCost = parseFloat(donor?.totalQCTestingCost || 0);
  const totalDonorCost = rawMaterialsCost + qcTestingCost;
  
  // Calculate total vials manufactured
  const totalVialsManufactured = (donor?.manufacturedVials || [])
    .reduce((sum, v) => sum + (v.quantity || 0), 0);
  
  // Calculate QC samples used (sterility and endotoxin only)
  const qcSamplesUsed = (donor?.qcSamples || [])
    .filter(s => s.sampleType === 'STERILITY' || s.sampleType === 'ENDOTOXIN')
    .reduce((sum, s) => sum + (s.quantity || 0), 0);
  
  // Calculate total vials available for sale (always calculate from manufactured - QC samples)
  const vialsAvailableForSale = Math.max(0, totalVialsManufactured - qcSamplesUsed);
  
  // Calculate cost per vial
  const costPerVial = vialsAvailableForSale > 0 ? (totalDonorCost / vialsAvailableForSale) : 0;

  // Don't render if no data available
  if (!totalDonorCost || !vialsAvailableForSale) {
    return null;
  }

  return (
    <Card sx={{ mb: 2, border: '2px solid', borderColor: 'primary.light' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalculateIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            Per Vial Cost Calculation
          </Typography>
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Total Cost</Typography>
            <Typography variant="h6" color="primary">
              ${totalDonorCost.toFixed(2)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={1} sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color="text.secondary">÷</Typography>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Vials for Sale</Typography>
            <Typography variant="h6" color="success.main">
              {vialsAvailableForSale} vials
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={1} sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color="text.secondary">=</Typography>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Cost Per Vial</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" color="secondary" fontWeight="bold">
                ${costPerVial.toFixed(2)}
              </Typography>
              <Chip 
                label="per vial" 
                size="small" 
                color="secondary" 
                variant="outlined" 
              />
            </Box>
          </Grid>
        </Grid>
        
        {/* Breakdown */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Cost Breakdown:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption">
                Raw Materials: ${rawMaterialsCost.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption">
                QC Testing: ${qcTestingCost.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerVialCalculator;